import React, { useMemo, useState } from 'react';
import type { SimWorld } from '../../simulation/types/SimWorld';
import type { SimNPC } from '../../simulation/types/SimNPC';

interface FamilyTreePanelProps {
  world: SimWorld;
  onNPCClick?: (npc: SimNPC) => void;
  isMobile?: boolean;
}

/**
 * 夫婦ノード（カップル単位で表示）
 */
interface CoupleNode {
  id: string; // ユニークID (npc1.id + npc2?.id)
  primary: SimNPC; // メインNPC
  partner: SimNPC | null; // パートナー（いない場合はnull）
  children: CoupleNode[]; // 子供たち（それぞれがCoupleNode）
  generation: number;
}

/**
 * 家系図表示パネル（夫婦ペア形式）
 */
export const FamilyTreePanel: React.FC<FamilyTreePanelProps> = ({ world, onNPCClick, isMobile = false }) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // 家系図構築（夫婦ペア形式）
  const familyTrees = useMemo(() => {
    const npcMap = new Map<string, SimNPC>();
    world.npcs.forEach((npc) => npcMap.set(npc.id, npc));

    // 処理済みNPCを追跡（重複防止）
    const processedNPCs = new Set<string>();

    // パートナーを取得
    const getPartner = (npc: SimNPC): SimNPC | null => {
      const partnerRel = npc.family.find((f) => f.relationType === 'partner');
      return partnerRel ? npcMap.get(partnerRel.relationId) || null : null;
    };

    // 子供を取得（両親のどちらかから）
    const getChildren = (npc: SimNPC): SimNPC[] => {
      const childRelations = npc.family.filter((f) => f.relationType === 'child');
      return childRelations
        .map((rel) => npcMap.get(rel.relationId))
        .filter((child): child is SimNPC => child !== undefined);
    };

    // カップルノードを構築
    const buildCoupleNode = (npc: SimNPC, generation: number): CoupleNode | null => {
      // 既に処理済みならスキップ
      if (processedNPCs.has(npc.id)) {
        return null;
      }

      const partner = getPartner(npc);

      // NPCとパートナーを処理済みにマーク
      processedNPCs.add(npc.id);
      if (partner) {
        processedNPCs.add(partner.id);
      }

      // 子供を取得（NPCの子供リストから）
      const children = getChildren(npc);

      // 子供のカップルノードを構築
      const childNodes = children
        .map((child) => buildCoupleNode(child, generation + 1))
        .filter((node): node is CoupleNode => node !== null);

      return {
        id: partner ? `${npc.id}_${partner.id}` : npc.id,
        primary: npc,
        partner,
        children: childNodes,
        generation,
      };
    };

    // 初代NPCを探す（親がいないNPC）
    const founders = world.npcs.filter((npc) => {
      return !npc.family.some((f) => f.relationType === 'parent');
    });

    // 初代NPCからツリーを構築
    const trees: CoupleNode[] = [];
    for (const founder of founders) {
      if (!processedNPCs.has(founder.id)) {
        const node = buildCoupleNode(founder, 0);
        if (node) {
          trees.push(node);
        }
      }
    }

    return trees;
  }, [world.npcs]);

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allIds = new Set<string>();
    const collectIds = (node: CoupleNode) => {
      allIds.add(node.id);
      node.children.forEach(collectIds);
    };
    familyTrees.forEach(collectIds);
    setExpandedNodes(allIds);
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>🌳 家系図</h3>
        <div style={styles.controls}>
          <button onClick={expandAll} style={styles.controlButton}>
            すべて展開
          </button>
          <button onClick={collapseAll} style={styles.controlButton}>
            すべて折りたたむ
          </button>
        </div>
      </div>

      <div style={styles.treeContainer}>
        {familyTrees.length === 0 ? (
          <div style={styles.emptyMessage}>家系図データがありません</div>
        ) : (
          familyTrees.map((tree) => (
            <CoupleNodeView
              key={tree.id}
              node={tree}
              expandedNodes={expandedNodes}
              onToggle={toggleNode}
              onNPCClick={onNPCClick}
            />
          ))
        )}
      </div>

      <div style={styles.footer}>
        <span style={styles.legend}>
          <span style={{ ...styles.legendItem, color: '#4ade80' }}>● 生存</span>
          <span style={{ ...styles.legendItem, color: '#ef4444' }}>● 死亡</span>
        </span>
      </div>
    </div>
  );
};

/**
 * カップルノードビュー
 */
interface CoupleNodeViewProps {
  node: CoupleNode;
  expandedNodes: Set<string>;
  onToggle: (nodeId: string) => void;
  onNPCClick?: (npc: SimNPC) => void;
}

const CoupleNodeView: React.FC<CoupleNodeViewProps> = ({
  node,
  expandedNodes,
  onToggle,
  onNPCClick,
}) => {
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children.length > 0;
  const indentLevel = node.generation;

  return (
    <div style={coupleStyles.container}>
      {/* カップル行 */}
      <div
        style={{
          ...coupleStyles.coupleRow,
          paddingLeft: `${indentLevel * 28}px`,
        }}
      >
        {/* 展開/折りたたみボタン */}
        <button
          onClick={() => onToggle(node.id)}
          style={{
            ...coupleStyles.expandButton,
            visibility: hasChildren ? 'visible' : 'hidden',
          }}
        >
          {isExpanded ? '▼' : '▶'}
        </button>

        {/* メインNPC */}
        <NPCBadge npc={node.primary} onClick={onNPCClick} />

        {/* パートナー（いる場合） */}
        {node.partner && (
          <>
            <span style={coupleStyles.connector}>═</span>
            <NPCBadge npc={node.partner} onClick={onNPCClick} />
          </>
        )}

        {/* 子供数表示 */}
        {hasChildren && (
          <span style={coupleStyles.childCount}>👶 {node.children.length}</span>
        )}
      </div>

      {/* 子供たち */}
      {isExpanded && hasChildren && (
        <div style={coupleStyles.childrenContainer}>
          {/* 接続線 */}
          <div
            style={{
              ...coupleStyles.verticalLine,
              left: `${indentLevel * 28 + 18}px`,
            }}
          />
          {node.children.map((child, index) => (
            <div key={child.id} style={coupleStyles.childWrapper}>
              {/* 横線 */}
              <div
                style={{
                  ...coupleStyles.horizontalLine,
                  left: `${indentLevel * 28 + 18}px`,
                  width: '10px',
                }}
              />
              <CoupleNodeView
                node={child}
                expandedNodes={expandedNodes}
                onToggle={onToggle}
                onNPCClick={onNPCClick}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * NPCバッジ（個人表示）
 */
interface NPCBadgeProps {
  npc: SimNPC;
  onClick?: (npc: SimNPC) => void;
}

const NPCBadge: React.FC<NPCBadgeProps> = ({ npc, onClick }) => {
  const winRate = npc.wins + npc.losses > 0
    ? Math.round((npc.wins / (npc.wins + npc.losses)) * 100)
    : 0;

  return (
    <div
      style={{
        ...badgeStyles.badge,
        borderColor: npc.isAlive ? '#4ade80' : '#ef4444',
        opacity: npc.isAlive ? 1 : 0.7,
      }}
      onClick={() => onClick?.(npc)}
      title={`${npc.name}\n戦績: ${npc.wins}勝${npc.losses}敗 (${winRate}%)\n生存: ${npc.daysLived}日`}
    >
      {/* 生存インジケータ */}
      <div
        style={{
          ...badgeStyles.statusDot,
          backgroundColor: npc.isAlive ? '#4ade80' : '#ef4444',
        }}
      />

      {/* 名前 */}
      <span style={badgeStyles.name}>
        {npc.name}
        {!npc.isAlive && ' 💀'}
      </span>

      {/* 勝率 */}
      <span
        style={{
          ...badgeStyles.winRate,
          color: winRate >= 50 ? '#4ade80' : winRate >= 30 ? '#facc15' : '#ef4444',
        }}
      >
        {winRate}%
      </span>
    </div>
  );
};

// NPCバッジスタイル
const badgeStyles: Record<string, React.CSSProperties> = {
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    backgroundColor: '#1e1e3f',
    border: '2px solid',
    borderRadius: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontSize: '13px',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  name: {
    color: '#ddd',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  winRate: {
    fontSize: '11px',
    fontWeight: 'bold',
  },
};

// カップルノードスタイル
const coupleStyles: Record<string, React.CSSProperties> = {
  container: {
    position: 'relative',
  },
  coupleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '4px',
  },
  expandButton: {
    width: '20px',
    height: '20px',
    border: 'none',
    background: 'transparent',
    color: '#888',
    cursor: 'pointer',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  connector: {
    color: '#666',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  childCount: {
    marginLeft: '8px',
    fontSize: '12px',
    color: '#888',
  },
  childrenContainer: {
    position: 'relative',
    marginLeft: '0px',
  },
  childWrapper: {
    position: 'relative',
  },
  verticalLine: {
    position: 'absolute',
    top: 0,
    bottom: '50%',
    width: '2px',
    backgroundColor: '#444',
  },
  horizontalLine: {
    position: 'absolute',
    top: '50%',
    height: '2px',
    backgroundColor: '#444',
  },
};

// パネルスタイル
const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#1a1a2e',
    border: '2px solid #444',
    borderRadius: '8px',
    height: '600px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#0f0f1e',
    borderBottom: '1px solid #444',
  },
  title: {
    margin: 0,
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  controls: {
    display: 'flex',
    gap: '8px',
  },
  controlButton: {
    padding: '6px 12px',
    backgroundColor: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '4px',
    color: '#aaa',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  treeContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px',
  },
  emptyMessage: {
    color: '#666',
    fontSize: '14px',
    textAlign: 'center',
    padding: '40px 20px',
  },
  footer: {
    padding: '8px 16px',
    backgroundColor: '#0f0f1e',
    borderTop: '1px solid #444',
    display: 'flex',
    justifyContent: 'center',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    fontSize: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
};
